import { ActivatedRoute } from '@angular/router';
import { Component, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';
import { MakeOutBoundCallNode } from '@modules/campaigns/Domain/Nodes/MakeOutBoundCall';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { MakeoutboundcallService } from '@shared/components/joint-js/services/makeoutboundcall.service';
import { CampaignNewService } from '@modules/campaigns-new/Application/campaign_new.service';
import { OrderCampaignsService } from '@shared/services/orderCampaigns.service';
import { CAMPAIGN_TYPE } from '@modules/campaigns-new/Domain/campaing.model';
import { TreeNode } from '@shared/components/tree-select/Domain/treeNode.model';
import {
  ActionBranch,
  MakeOutBoundCallConfig,
  RetryTimeRange,
} from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { getValidationErrorMessage } from '@modules/campaigns/Domain/Enums/ValidationErrors';

import { DropdownModule } from 'primeng/dropdown';
import { ChipsModule } from 'primeng/chips';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CardModule } from 'primeng/card';
import { MessagesModule } from 'primeng/messages';
import { TreeSelectComponent } from '@shared/components/tree-select/tree-select.component';
import { ToastService } from '@services/ui';

const DEFAULT_RETRY_RANGES: RetryTimeRange[] = [
  { label: 'Reintento 1', from: '08:00', to: '12:00', selected: false },
  { label: 'Reintento 2', from: '13:00', to: '16:00', selected: false },
  { label: 'Reintento 3', from: '17:00', to: '20:00', selected: false },
];

@Component({
  selector: 'app-make-outbound-call-inspector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DropdownModule,
    ChipsModule,
    CheckboxModule,
    InputTextModule,
    ButtonModule,
    DividerModule,
    SelectButtonModule,
    CardModule,
    MessagesModule,
    TreeSelectComponent,
  ],
  templateUrl: './make-outbound-call-inspector.component.html',
  styleUrls: ['./make-outbound-call-inspector.component.scss'],
})
export class MakeOutBoundCallComponent extends InspectorBase<MakeOutBoundCallNode> {
  agents = signal<any[]>([]);
  selectedAgentId = signal<string | null>(null);
  selectedAgentDetails = computed(() => this.agents().find((a) => a.id === this.selectedAgentId()));

  isContactAll = signal<boolean>(false);
  isRetryFailCalls = signal<boolean>(false);

  retryTimeRanges = signal<RetryTimeRange[]>([
    { label: 'Reintento 1', from: '', to: '', selected: false },
    { label: 'Reintento 2', from: '', to: '', selected: false },
    { label: 'Reintento 3', from: '', to: '', selected: false },
  ]);

  actions = signal<ActionBranch[]>([]);
  editingActions = signal<Set<number>>(new Set());

  campaignOptions = signal<TreeNode[]>([]);

  constructor(
    private toast: ToastService,
    private translate: TranslateService,
    private workflowState: WorkflowStateService,
    private callService: MakeoutboundcallService,
    private campaignNewService: CampaignNewService,
    private orderCampaignsService: OrderCampaignsService,
    private route: ActivatedRoute,
  ) {
    super();
    this.loadAgents();
    this.loadCampaignOptions();
  }

  get nodeValue(): MakeOutBoundCallNode | null {
    return this.node() as MakeOutBoundCallNode;
  }

  getTabs(): { label: string; value: 'preview' | 'config' }[] {
    return [
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.MAKE_OUTBOUND_CALL.TABS.PREVIEW'),
        value: 'preview',
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.MAKE_OUTBOUND_CALL.TABS.CONFIG'),
        value: 'config',
      },
    ];
  }

  protected refreshFromConfig() {
    const config = this.nodeValue?.config as MakeOutBoundCallConfig;
    if (!config) return;

    this.selectedAgentId.set((config as MakeOutBoundCallConfig).agentId || null);
    this.isContactAll.set(
      (config as MakeOutBoundCallConfig)['callToAllContacts'] ?? config.isContactAll ?? false,
    );

    const apiIntervals: { start: string; finish: string }[] | undefined = (config as any)
      .callIntervals;

    let derivedIsRetry: boolean;

    if (Array.isArray(apiIntervals) && apiIntervals.length > 0) {
      const ranges = DEFAULT_RETRY_RANGES.map((r, idx) => {
        const interval = apiIntervals[idx];
        if (interval) {
          return { ...r, from: interval.start, to: interval.finish, selected: true };
        }
        return { ...r };
      });

      this.retryTimeRanges.set(ranges);
      derivedIsRetry = true;
    } else if (config.retryTimeRanges && config.retryTimeRanges.length > 0) {
      this.retryTimeRanges.set(JSON.parse(JSON.stringify(config.retryTimeRanges)));
      derivedIsRetry = (config.retryTimeRanges as RetryTimeRange[]).some((r) => r.selected);
    } else {
      this.retryTimeRanges.set(JSON.parse(JSON.stringify(DEFAULT_RETRY_RANGES)));
      derivedIsRetry = config.isRetryFailCalls ?? false;
    }

    const explicitFlag = (config as any).isRetryFailCalls;
    this.isRetryFailCalls.set(explicitFlag !== undefined ? explicitFlag : derivedIsRetry);

    const apiActions: any[] | undefined = (config as any).actions ?? config.actions ?? [];
    const uiActions: ActionBranch[] = (apiActions || []).map((a: any) => ({
      situation: a.conversationResult ?? a.situation ?? '',
      action:
        a.action === 'feedback' ? 'feedback' : a.action === 'endFeedback' ? 'feedback' : a.action,
      campaign: a.parameters?.workflowId || a.campaign,
    }));

    this.actions.set(uiActions);
  }

  private loadAgents() {
    this.callService.searchPhoneCallAgents().subscribe((agents) => {
      this.agents.set(agents);
    });
  }

  private loadCampaignOptions() {
    this.campaignNewService
      .getCampaignList([CAMPAIGN_TYPE.DRIP, CAMPAIGN_TYPE.PILL])
      .subscribe((campaigns: any[]) => {
        const treeNodes: TreeNode[] = this.orderCampaignsService.parseCampaignsToTree(
          campaigns,
          null,
        );
        this.campaignOptions.set(treeNodes);
      });
  }

  onAgentChange(agentId: string) {
    this.selectedAgentId.set(agentId);
    this.applyChanges();
  }

  onRetryRangeChange(index?: number) {
    if (typeof index === 'number') {
      const current = this.retryTimeRanges()[index];
      if (current && current.selected && (!current.from || !current.to)) {
        const defaults = [
          { from: '08:00', to: '12:00' },
          { from: '13:00', to: '16:00' },
          { from: '17:00', to: '20:00' },
        ];
        const def = defaults[index] || { from: '08:00', to: '12:00' };
        current.from = def.from;
        current.to = def.to;
        this.retryTimeRanges.set([...this.retryTimeRanges()]);
      }
    }

    const anySelected = this.retryTimeRanges().some((r) => r.selected);
    this.isRetryFailCalls.set(anySelected);

    this.applyChanges();
  }

  addAction() {
    const currentActions = this.actions();
    const newIndex = currentActions.length;
    this.actions.set([...currentActions, { situation: '', action: 'feedback' }]);
    this.editingActions.update((set) => {
      const newSet = new Set(set);
      newSet.add(newIndex);
      return newSet;
    });

    setTimeout(() => {
      const element = document.getElementById(`action-form-${newIndex}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = element.querySelector('input');
        if (input) input.focus();
      }
    }, 100);
  }

  saveAction(index: number) {
    this.editingActions.update((set) => {
      const newSet = new Set(set);
      newSet.delete(index);
      return newSet;
    });
    this.applyChanges();
  }

  editAction(index: number) {
    this.editingActions.update((set) => {
      const newSet = new Set(set);
      newSet.add(index);
      return newSet;
    });
  }

  removeAction(index: number) {
    const currentActions = this.actions();
    currentActions.splice(index, 1);
    this.actions.set([...currentActions]);

    this.editingActions.update((set) => {
      const newSet = new Set<number>();
      set.forEach((idx) => {
        if (idx < index) newSet.add(idx);
        if (idx > index) newSet.add(idx - 1);
      });
      return newSet;
    });

    this.applyChanges();
  }

  onActionChange(index: number, field: string, value: any) {
    const currentActions = this.actions();
    (currentActions[index] as any)[field] = value;
    this.actions.set([...currentActions]);
    this.applyChanges();
  }

  onCampaignSelectionChange(index: number, nodes: TreeNode[]) {
    if (nodes.length > 0) {
      this.onActionChange(index, 'campaign', nodes[0].key);
    } else {
      this.onActionChange(index, 'campaign', null);
    }
  }

  getCampaignName(campaignId: string | undefined): string {
    if (!campaignId) return '-';
    const findInNodes = (nodes: TreeNode[]): string | null => {
      for (const node of nodes) {
        if (node.key === campaignId) return node.label;
        if (node.children) {
          const found = findInNodes(node.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findInNodes(this.campaignOptions()) || campaignId;
  }

  viewCampaign(campaignId: string | undefined) {
    if (!campaignId) return;
    const tenantId = this.route.snapshot.queryParams['tenant'];
    const url = `/campaigns/edit/${campaignId}${tenantId ? '?tenant=' + tenantId : ''}`;
    window.open(url, '_blank');
  }

  private buildIntervalsFromRanges() {
    return this.retryTimeRanges()
      .filter((r) => r.selected)
      .map((r) => ({ from: r.from, to: r.to }));
  }

  private buildUpdatedConfig(): MakeOutBoundCallConfig {
    const node = this.nodeValue!;

    const intervals = this.buildIntervalsFromRanges();

    return {
      ...node.config,
      agentId: this.selectedAgentId() || '',
      isContactAll: this.isContactAll(),
      isRetryFailCalls: intervals.length > 0 || this.isRetryFailCalls(),
      retryTimeRanges: this.retryTimeRanges(),
      actions: this.actions(),
      ...(intervals.length > 0 ? { intervals } : {}),
      callToAllContacts: this.isContactAll(),
    } as any;
  }

  private buildUpdatedNode(updatedConfig: MakeOutBoundCallConfig) {
    const node = this.nodeValue!;
    const updatedNode = new MakeOutBoundCallNode(node.id, node.position, updatedConfig);
    updatedNode.parentNodes = [...node.parentNodes];
    updatedNode.childrenNodes = [...node.childrenNodes];
    return updatedNode;
  }

  private handleValidationErrors(validationErrors: any[]) {
    this.errors.set(validationErrors);
    if (validationErrors.length > 0) {
      try {
        this.activeTab.set('config');
        setTimeout(() => {
          const el = document.getElementById('make-outbound-errors');
          if (el && typeof (el as HTMLElement).focus === 'function') (el as HTMLElement).focus();
        }, 50);
      } catch (e) {}
      return true;
    }
    return false;
  }

  applyChanges() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = this.node()!.config || {};
    cfg['isRetryFailCalls'] = this.isRetryFailCalls();

    const intervals = this.buildIntervalsFromRanges();
    if (this.isRetryFailCalls()) {
      if (intervals.length) {
        cfg['callIntervals'] = intervals;
      } else {
        delete cfg['callIntervals'];
      }
    } else {
      delete cfg['callIntervals'];
    }

    const updatedConfig = this.buildUpdatedConfig();
    const updatedNode = this.buildUpdatedNode(updatedConfig);

    const validationErrors = updatedNode.validate();

    if (this.handleValidationErrors(validationErrors)) return;

    this.workflowState.addOrUpdateNode(updatedNode);
  }

  copyToClipboard(text: string) {
    if (!text) return;
    navigator.clipboard.writeText(text);
    this.toast.showInfo(
      text,
      this.translate.instant('DATA.MODULES.CUSTOMERS.PAGES.SECTIONS.COPIED_SUCCESSFULLY'),
    );
  }

  getErrorMessage(error: any): string {
    return getValidationErrorMessage(
      error.code || error.errorMessage,
      error.params,
      this.translate,
    );
  }
}
